import { usePage } from '@inertiajs/react'
import { Check } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Avatar } from '@/components/design-system/Avatar'
import { cn } from '@/lib/utils'
import { parseWhatsAppInput } from '@/frontend/utils/whatsappMask'
import type { SharedProps } from '@/frontend/types/inertia'
import type { BaseStepProps } from '@/frontend/pages/Dashboard/DigitalWorkers/new/types'

interface Props extends BaseStepProps {
  showWhatsApp?: boolean
}

export default function ResponsibleStep({ data, errors, disabled = false, onFieldChange, showWhatsApp = true }: Props) {
  const { auth } = usePage<SharedProps>().props
  const userName = auth?.user?.name ?? ''
  const avatarName = data.contact_name.trim() || userName
  const isCurrentUser = data.contact_name.trim() === userName.trim() && userName.length > 0

  const whatsappDigits = data.contact_whatsapp.replace(/\D/g, '')
  const whatsappComplete = whatsappDigits.length >= 12 // 55 + DDD(2) + 8-9 digits
  const whatsappStarted = whatsappDigits.length > 0

  return (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <h2 className="text-2xl font-semibold tracking-tight text-foreground">Defina um responsável</h2>
        <p className="text-sm text-muted-foreground">Escolha quem vai ser a pessoa responsável por esse agente.</p>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="contact-name">Responsável</Label>
        <div className="relative">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2"
          >
            <Avatar name={avatarName} size="sm" />
          </span>
          <Input
            id="contact-name"
            value={data.contact_name}
            onChange={(event) => onFieldChange('contact_name', event.target.value)}
            placeholder="Ex: Jon Snow"
            disabled={disabled}
            className="pl-12"
          />
        </div>
        {isCurrentUser && (
          <p className="text-xs text-muted-foreground">
            Esse é você. Troque o nome acima se outra pessoa for responsável.
          </p>
        )}
        {errors.contact_name && <p className="text-sm text-destructive">{errors.contact_name}</p>}
      </div>

      {showWhatsApp && <div className="grid gap-2">
        <Label htmlFor="contact-whatsapp">WhatsApp do responsável</Label>
        <div className="relative">
          <Input
            id="contact-whatsapp"
            value={data.contact_whatsapp}
            onChange={(event) => onFieldChange('contact_whatsapp', parseWhatsAppInput(event.target.value))}
            placeholder="+55 11 99999-9999"
            disabled={disabled}
            className={cn(
              'pr-9 transition-all duration-300',
              whatsappStarted && !whatsappComplete && 'border-violet-400/70 focus-visible:border-violet-500',
              whatsappComplete && 'border-green-500 ring-2 ring-green-500/30 focus-visible:border-green-500',
            )}
          />
          {whatsappComplete && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full bg-green-500 text-white animate-check-zoom-in"
            >
              <Check className="h-3 w-3" />
            </span>
          )}
        </div>
        {errors.contact_whatsapp && <p className="text-sm text-destructive">{errors.contact_whatsapp}</p>}
      </div>}
    </div>
  )
}
